import Layout from "@/components/layouts/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useLanguage } from "@/contexts/LanguageContext";
import Reveal from "@/components/Reveal";
import {
  Target,
  Eye,
  Users,
  Lightbulb,
  Shield,
  Rocket,
  Award,
  ArrowRight,
  Sparkles,
  Compass,
  GraduationCap,
  UserCircle,
  MessagesSquare,
  BookOpen,
  Briefcase,
  Wrench,
  Building2,
  Share2,
  TrendingUp,
} from "lucide-react";
import { Link } from "react-router-dom";
import aboutHeroPeople from "@/assets/about-hero-people.jpg";
import aboutStoryPeople from "@/assets/about-story-people.jpg";
import { cn } from "@/lib/utils";
import CtaSection from "@/components/sections/ctaSection";

const AboutUs = () => {
  const { t } = useLanguage();

  const values = [
    {
      icon: TrendingUp,
      title: t("about.values.growth.title"),
      description: t("about.values.growth.description"),
    },
    {
      icon: Users,
      title: t("about.values.community.title"),
      description: t("about.values.community.description"),
    },
    {
      icon: Lightbulb,
      title: t("about.values.innovation.title"),
      description: t("about.values.innovation.description"),
    },
    {
      icon: Shield,
      title: t("about.values.quality.title"),
      description: t("about.values.quality.description"),
    },
  ];

  const ecosystem = [
    {
      icon: UserCircle,
      title: t("about.ecosystem.profiles.title"),
      description: t("about.ecosystem.profiles.description"),
    },
    {
      icon: MessagesSquare,
      title: t("about.ecosystem.feed.title"),
      description: t("about.ecosystem.feed.description"),
    },
    {
      icon: Share2,
      title: t("about.ecosystem.learning.title"),
      description: t("about.ecosystem.learning.description"),
    },
    {
      icon: GraduationCap,
      title: t("about.ecosystem.mentorship.title"),
      description: t("about.ecosystem.mentorship.description"),
    },
    {
      icon: BookOpen,
      title: t("about.ecosystem.study.title"),
      description: t("about.ecosystem.study.description"),
    },
    {
      icon: Briefcase,
      title: t("about.ecosystem.jobs.title"),
      description: t("about.ecosystem.jobs.description"),
    },
    {
      icon: Building2,
      title: t("about.ecosystem.companies.title"),
      description: t("about.ecosystem.companies.description"),
    },
    {
      icon: Wrench,
      title: t("about.ecosystem.services.title"),
      description: t("about.ecosystem.services.description"),
    },
  ];

  // Brand accent rotation: teal -> yellow -> red
  const accents = [
    {
      text: "text-nexus-teal",
      bg: "bg-nexus-teal",
      soft: "bg-nexus-teal/10",
      ring: "ring-nexus-teal/20",
    },
    {
      text: "text-nexus-yellow",
      bg: "bg-nexus-yellow",
      soft: "bg-nexus-yellow/15",
      ring: "ring-nexus-yellow/20",
    },
    {
      text: "text-nexus-red",
      bg: "bg-nexus-red",
      soft: "bg-nexus-red/10",
      ring: "ring-nexus-red/20",
    },
  ];

  const milestones = [
    {
      year: t("about.timeline.late2024.year"),
      title: t("about.timeline.late2024.title"),
      description: t("about.timeline.late2024.description"),
    },
    {
      year: t("about.timeline.2025_announce.year"),
      title: t("about.timeline.2025_announce.title"),
      description: t("about.timeline.2025_announce.description"),
    },
    {
      year: t("about.timeline.2025_meetups.year"),
      title: t("about.timeline.2025_meetups.title"),
      description: t("about.timeline.2025_meetups.description"),
    },
    {
      year: t("about.timeline.2025_services.year"),
      title: t("about.timeline.2025_services.title"),
      description: t("about.timeline.2025_services.description"),
    },
    {
      year: t("about.timeline.2025_blog.year"),
      title: t("about.timeline.2025_blog.title"),
      description: t("about.timeline.2025_blog.description"),
    },
    {
      year: t("about.timeline.late2025_platform.year"),
      title: t("about.timeline.late2025_platform.title"),
      description: t("about.timeline.late2025_platform.description"),
    },
    {
      year: t("about.timeline.soon_community.year"),
      title: t("about.timeline.soon_community.title"),
      description: t("about.timeline.soon_community.description"),
    },
    {
      year: t("about.timeline.soon_company.year"),
      title: t("about.timeline.soon_company.title"),
      description: t("about.timeline.soon_company.description"),
    },
    {
      year: t("about.timeline.soon_crowd.year"),
      title: t("about.timeline.soon_crowd.title"),
      description: t("about.timeline.soon_crowd.description"),
    },
  ];

  return (
    <Layout>
      <div className="relative overflow-hidden">
        {/* Floating background accents */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div
            className={cn(
              "absolute -right-24 -top-24 h-96 w-96 rounded-full",
              "bg-nexus-teal/20 blur-3xl animate-float-slow",
            )}
          />
          <div
            className={cn(
              "absolute -left-24 top-1/3 h-72 w-72 rounded-full",
              "bg-nexus-yellow/15 blur-3xl animate-float-slow",
            )}
            style={{ animationDelay: "2s" }}
          />
          <div
            className={cn(
              "absolute bottom-0 right-1/4 h-80 w-80 rounded-full",
              "bg-nexus-red/10 blur-3xl animate-float-slow",
            )}
            style={{ animationDelay: "4s" }}
          />
        </div>

        {/* Hero Section */}
        <section className="relative pt-28 pb-20 lg:pt-36 lg:pb-28">
          <div className="container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <Reveal from="left" className="order-2 lg:order-1">
                <div className="space-y-6">
                  <div
                    className={cn(
                      "inline-flex items-center gap-2 rounded-full px-4 py-2",
                      "bg-nexus-teal/10 text-sm font-medium text-nexus-teal",
                    )}
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>{t("about.hero.tagline")}</span>
                  </div>
                  <h1
                    className={cn(
                      "text-4xl font-bold leading-tight text-nexus-gray",
                      "dark:text-white md:text-5xl lg:text-6xl",
                    )}
                  >
                    {t("about.hero.title")}
                  </h1>
                  <p className="text-lg text-muted-foreground dark:text-white/70">
                    {t("about.hero.subtitle")}
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Button asChild className="bg-nexus-brand hover:bg-nexus-brand/90">
                      <Link to="/community">
                        {t("about.hero.primaryCta")}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="dark:border-white/20 dark:text-white dark:hover:bg-white/10"
                    >
                      <Link to="/contact">{t("about.hero.secondaryCta")}</Link>
                    </Button>
                  </div>
                </div>
              </Reveal>
              <Reveal from="right" className="order-1 lg:order-2">
                <div className="relative aspect-square max-w-md mx-auto lg:max-w-none">
                  <div
                    className={cn(
                      "absolute inset-0 rounded-3xl border border-nexus-teal/20",
                      "bg-gradient-to-br from-nexus-teal/20",
                      "via-nexus-yellow/10 to-nexus-red/15",
                    )}
                  />
                  <img
                    src={aboutHeroPeople}
                    alt={t("about.hero.imageAlt")}
                    width={1024}
                    height={1024}
                    className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                  />
                  <div
                    className={cn(
                      "absolute -right-4 -top-4 rounded-2xl p-3 shadow-lg",
                      "bg-white animate-float-slow dark:bg-nexus-dark",
                    )}
                  >
                    <Rocket className="w-6 h-6 text-nexus-teal" />
                  </div>
                  <div
                    className={cn(
                      "absolute -bottom-4 -left-4 rounded-2xl p-3 shadow-lg",
                      "bg-white animate-float-slow dark:bg-nexus-dark",
                    )}
                    style={{ animationDelay: "1.5s" }}
                  >
                    <Award className="w-6 h-6 text-nexus-yellow" />
                  </div>
                  <div
                    className={cn(
                      "absolute -right-6 top-1/2 rounded-2xl p-3 shadow-lg",
                      "bg-white animate-float-slow dark:bg-nexus-dark",
                    )}
                    style={{ animationDelay: "3s" }}
                  >
                    <GraduationCap className="w-6 h-6 text-nexus-red" />
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Our Story Section */}
        <section className="relative py-20 lg:py-28">
          <div className="container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <Reveal from="left" className="order-1">
                <div className="relative">
                  <div
                    className={cn(
                      "relative aspect-[4/3] overflow-hidden rounded-3xl",
                      "border border-nexus-teal/20",
                    )}
                  >
                    <img
                      src={aboutStoryPeople}
                      alt={t("about.story.imageAlt")}
                      loading="lazy"
                      width={1280}
                      height={960}
                      className="w-full h-full object-cover"
                    />
                    <div
                      className={cn(
                        "absolute inset-x-0 bottom-0 p-6",
                        "bg-gradient-to-t from-black/80 via-black/40 to-transparent",
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <Compass className="w-6 h-6 text-nexus-yellow shrink-0 mt-1" />
                        <blockquote className="text-base md:text-lg font-medium text-white italic">
                          "{t("about.story.quote")}"
                        </blockquote>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
              <Reveal from="right" className="order-2">
                <div className="space-y-6">
                  <span className="text-nexus-teal font-semibold uppercase tracking-wide text-sm">
                    {t("about.story.label")}
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold text-nexus-gray dark:text-white">
                    {t("about.story.title")}
                  </h2>
                  <div className="space-y-4 text-muted-foreground dark:text-white/70">
                    <p>{t("about.story.paragraph1")}</p>
                    <p>{t("about.story.paragraph2")}</p>
                    <p>{t("about.story.paragraph3")}</p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Ecosystem Section */}
        <section className="relative py-20 lg:py-28">
          <div className="container">
            <Reveal>
              <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="text-nexus-teal font-semibold uppercase tracking-wide text-sm">
                  {t("about.ecosystem.label")}
                </span>
                <h2
                  className={cn(
                    "mb-6 mt-3 text-3xl font-bold text-nexus-gray",
                    "dark:text-white md:text-4xl",
                  )}
                >
                  {t("about.ecosystem.title")}
                </h2>
                <p className="text-lg text-muted-foreground dark:text-white/70">
                  {t("about.ecosystem.subtitle")}
                </p>
              </div>
            </Reveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {ecosystem.map((item, index) => {
                const a = accents[index % accents.length];
                return (
                  <Reveal key={index} delay={index * 100}>
                    <Card
                      className={cn(
                        "group relative h-full overflow-hidden border shadow-md",
                        "border-nexus-teal/10 bg-white/80 backdrop-blur-sm",
                        "transition-all duration-300 hover:-translate-y-2",
                        "dark:border-white/10 dark:bg-white/5",
                      )}
                    >
                      <div
                        className={cn(
                          "absolute inset-x-0 top-0 h-1 origin-left scale-x-0",
                          "transition-transform duration-500 group-hover:scale-x-100",
                          a.bg,
                        )}
                      />
                      <div
                        className={cn(
                          "absolute -right-12 -top-12 h-32 w-32 rounded-full",
                          "opacity-0 blur-2xl transition-opacity duration-500",
                          "group-hover:opacity-100",
                          a.soft,
                        )}
                      />
                      <CardContent className="relative p-6">
                        <div
                          className={cn(
                            "mb-4 flex h-12 w-12 items-center justify-center rounded-xl",
                            "ring-1 transition-all duration-300 group-hover:scale-110",
                            a.soft,
                            a.ring,
                          )}
                        >
                          <item.icon className={`w-6 h-6 ${a.text}`} />
                        </div>
                        <h3 className="text-xl font-bold text-nexus-gray dark:text-white mb-2">
                          {item.title}
                        </h3>
                        <p className="text-muted-foreground dark:text-white/70">
                          {item.description}
                        </p>
                      </CardContent>
                    </Card>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="relative py-20 lg:py-28">
          <div className="container">
            <Reveal>
              <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="text-nexus-teal font-semibold uppercase tracking-wide text-sm">
                  {t("about.missionVision.label")}
                </span>
                <h2
                  className={cn(
                    "mb-6 mt-3 text-3xl font-bold text-nexus-gray",
                    "dark:text-white md:text-4xl",
                  )}
                >
                  {t("about.missionVision.title")}
                </h2>
                <p className="text-lg text-muted-foreground dark:text-white/70">
                  {t("about.missionVision.subtitle")}
                </p>
              </div>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Reveal from="left" delay={100}>
                <Card
                  className={cn(
                    "h-full border-none bg-white/80 shadow-lg backdrop-blur-sm",
                    "dark:bg-white/5",
                  )}
                >
                  <CardContent className="p-8">
                    <div
                      className={cn(
                        "mb-6 flex h-14 w-14 items-center justify-center",
                        "rounded-2xl bg-nexus-teal/10",
                      )}
                    >
                      <Target className="w-7 h-7 text-nexus-teal" />
                    </div>
                    <h3 className="text-2xl font-bold text-nexus-gray dark:text-white mb-4">
                      {t("about.missionVision.mission.title")}
                    </h3>
                    <p className="text-muted-foreground dark:text-white/70">
                      {t("about.missionVision.mission.description")}
                    </p>
                  </CardContent>
                </Card>
              </Reveal>
              <Reveal from="right" delay={200}>
                <Card
                  className={cn(
                    "h-full border-none bg-white/80 shadow-lg backdrop-blur-sm",
                    "dark:bg-white/5",
                  )}
                >
                  <CardContent className="p-8">
                    <div
                      className={cn(
                        "mb-6 flex h-14 w-14 items-center justify-center",
                        "rounded-2xl bg-nexus-yellow/15",
                      )}
                    >
                      <Eye className="w-7 h-7 text-nexus-yellow" />
                    </div>
                    <h3 className="text-2xl font-bold text-nexus-gray dark:text-white mb-4">
                      {t("about.missionVision.vision.title")}
                    </h3>
                    <p className="text-muted-foreground dark:text-white/70">
                      {t("about.missionVision.vision.description")}
                    </p>
                  </CardContent>
                </Card>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="relative py-20 lg:py-28">
          <div className="container">
            <Reveal>
              <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="text-nexus-teal font-semibold uppercase tracking-wide text-sm">
                  {t("about.values.label")}
                </span>
                <h2
                  className={cn(
                    "mb-6 mt-3 text-3xl font-bold text-nexus-gray",
                    "dark:text-white md:text-4xl",
                  )}
                >
                  {t("about.values.title")}
                </h2>
                <p className="text-lg text-muted-foreground dark:text-white/70">
                  {t("about.values.subtitle")}
                </p>
              </div>
            </Reveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => {
                const a = accents[index % accents.length];
                return (
                  <Reveal key={index} delay={index * 100}>
                    <Card
                      className={cn(
                        "group relative h-full overflow-hidden border shadow-md",
                        "border-nexus-teal/10 bg-white/80 backdrop-blur-sm",
                        "transition-all duration-300 hover:-translate-y-2",
                        "dark:border-white/10 dark:bg-white/5",
                      )}
                    >
                      <div className={`absolute inset-y-0 left-0 w-1 ${a.bg}`} />
                      <CardContent className="p-6 pl-7">
                        <div
                          className={cn(
                            "mb-4 flex h-12 w-12 items-center justify-center rounded-xl",
                            "ring-1 transition-all duration-300 group-hover:scale-110",
                            a.soft,
                            a.ring,
                          )}
                        >
                          <value.icon className={`w-6 h-6 ${a.text}`} />
                        </div>
                        <h3 className="text-xl font-bold text-nexus-gray dark:text-white mb-2">
                          {value.title}
                        </h3>
                        <p className="text-muted-foreground dark:text-white/70">
                          {value.description}
                        </p>
                      </CardContent>
                    </Card>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="relative py-20 lg:py-28">
          <div className="container">
            <Reveal>
              <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="text-nexus-teal font-semibold uppercase tracking-wide text-sm">
                  {t("about.timeline.label")}
                </span>
                <h2
                  className={cn(
                    "mb-6 mt-3 text-3xl font-bold text-nexus-gray",
                    "dark:text-white md:text-4xl",
                  )}
                >
                  {t("about.timeline.title")}
                </h2>
                <p className="text-lg text-muted-foreground dark:text-white/70">
                  {t("about.timeline.subtitle")}
                </p>
              </div>
            </Reveal>

            <div className="max-w-4xl mx-auto space-y-10">
              {milestones.map((milestone, index) => {
                const a = accents[index % accents.length];
                return (
                  <Reveal key={index} delay={index * 100}>
                    <div className="relative flex gap-8">
                      {/* Timeline line */}
                      <div className="hidden md:flex flex-col items-center">
                        <div className={`w-4 h-4 rounded-full ${a.bg} ring-4 ${a.ring}`} />
                        {index !== milestones.length - 1 && (
                          <div
                            className={cn(
                              "mt-4 w-0.5 flex-grow bg-gradient-to-b",
                              "from-nexus-teal/40 via-nexus-yellow/30 to-nexus-red/20",
                            )}
                          />
                        )}
                      </div>
                      <Card
                        className={cn(
                          "flex-1 border border-nexus-teal/10 bg-white/80 shadow-lg",
                          "backdrop-blur-sm transition-transform duration-300",
                          "hover:-translate-y-1 dark:border-white/10 dark:bg-white/5",
                        )}
                      >
                        <CardContent className="p-8">
                          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
                            <span
                              className={cn(
                                "inline-flex rounded-full px-3 py-1 text-sm font-bold",
                                a.soft,
                                a.text,
                              )}
                            >
                              {milestone.year}
                            </span>
                            <h3 className="text-xl font-bold text-nexus-gray dark:text-white">
                              {milestone.title}
                            </h3>
                          </div>
                          <Separator className="my-4 dark:bg-white/10" />
                          <p className="text-muted-foreground dark:text-white/70 leading-relaxed">
                            {milestone.description}
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        <CtaSection />
      </div>
    </Layout>
  );
};

export default AboutUs;
